import type { SettingsResponse } from '@ignite/types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  PortalShell,
  Spinner,
  toast,
  type PortalNavLinkProps,
} from '@ignite/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { ChevronDown, Globe2, LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { buildNavItems, buildUserMenuItems, type WorkbenchView } from '../../config/workbench';
import {
  apiClient,
  clearStoredSession,
  readStoredSessionIdentity,
  readStoredUser,
  type StoredUser,
} from '../../lib/auth';
import { disconnectWenanExtension } from '../../lib/extension';
import { useSettingsData } from '../../lib/settingsData';
import { usePlatformContext } from './PlatformContext';
import { NotificationDrawer } from './NotificationDrawer';
import { ChatProvider } from './chat/ChatProvider';
import { GlobalChat } from './chat/GlobalChat';
import { clearPersistedChatState } from './chat/chat-state';

type SidebarAccountSettings = {
  user: Pick<SettingsResponse['user'], 'display_name' | 'email'>;
  plan: Pick<SettingsResponse['plan'], 'display_name'>;
};

// 用 TanStack Router 的 Link 渲染导航项，走客户端路由（SPA）而非整页跳转，避免每次跳转整页重载
function renderRouterNavLink({ href, className, active, onClick, children }: PortalNavLinkProps) {
  return (
    <Link
      to={href}
      className={className}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

export function WorkbenchShell({ view, children }: { view: WorkbenchView; children: ReactNode }) {
  return (
    <ChatProvider>
      <WorkbenchShellContent view={view}>{children}</WorkbenchShellContent>
    </ChatProvider>
  );
}

function WorkbenchShellContent({ view, children }: { view: WorkbenchView; children: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storedUser = readStoredUser();
  const { settingsQuery } = useSettingsData();
  const { accountName, accountDetail, userLabel } = resolveSidebarAccount({
    settings: settingsQuery.data,
    storedUser,
    fallbackName: t('portal.account.guest'),
    loadingPlanLabel: t('portal.account.planLoading'),
    unavailablePlanLabel: t('portal.account.planUnavailable'),
    isLoading: settingsQuery.isLoading,
  });
  const [logoutInFlight, setLogoutInFlight] = useState(false);

  async function handleAccessAction() {
    if (logoutInFlight) return;
    setLogoutInFlight(true);
    try {
      await apiClient.logout();
      await disconnectWenanExtension().catch(() => undefined);
      const identity = readStoredSessionIdentity();
      clearPersistedChatState(
        identity
          ? `${identity.tenantId}:${identity.userId}`
          : (storedUser?.id ?? storedUser?.email ?? 'anonymous'),
      );
      clearStoredSession();
      await navigate({ to: '/login' });
    } catch {
      toast.error(t('portal.logout.error'));
      setLogoutInFlight(false);
    }
  }

  return (
    <>
      <div aria-busy={logoutInFlight} inert={logoutInFlight ? true : undefined}>
        <PortalShell
          brand="Wenan"
          sidebarBrandMark={null}
          editionLabel={t('portal.edition.workbench')}
          localeLabel={accountDetail}
          navLabel={t('portal.nav.label')}
          showSidebarNavLabel={false}
          userLabel={userLabel}
          accountName={accountName}
          accountDetail={accountDetail}
          navItems={buildNavItems(t, view)}
          userMenuItems={buildUserMenuItems(t, view)}
          authenticated
          accessActionLabel={t('portal.action.logout')}
          onAccessAction={handleAccessAction}
          collapseLabel={t('portal.sidebar.collapse')}
          expandLabel={t('portal.sidebar.expand')}
          actions={
            <>
              <PlatformSelector />
              <NotificationDrawer />
            </>
          }
          renderNavLink={renderRouterNavLink}
        >
          {children}
        </PortalShell>
        <GlobalChat />
      </div>
      {logoutInFlight ? (
        <div
          className="workbench-logout-overlay"
          role="status"
          aria-live="assertive"
          aria-atomic="true"
        >
          <Spinner className="motion-reduce:animate-none" role="presentation" aria-hidden="true" />
          <strong>{t('portal.logout.pending')}</strong>
        </div>
      ) : null}
    </>
  );
}

export function resolveSidebarAccount({
  settings,
  storedUser,
  fallbackName,
  loadingPlanLabel,
  unavailablePlanLabel,
  isLoading,
}: {
  settings: SidebarAccountSettings | undefined;
  storedUser: StoredUser | null;
  fallbackName: string;
  loadingPlanLabel: string;
  unavailablePlanLabel: string;
  isLoading: boolean;
}) {
  const storedDisplayName = storedUser?.display_name?.trim();
  const accountName =
    settings?.user.display_name.trim() || storedDisplayName || storedUser?.email || fallbackName;
  const accountDetail =
    settings?.plan.display_name.trim() || (isLoading ? loadingPlanLabel : unavailablePlanLabel);
  const userLabel = settings?.user.email ?? storedUser?.email ?? fallbackName;

  return { accountName, accountDetail, userLabel };
}

function PlatformSelector() {
  const { t } = useTranslation();
  const {
    platforms,
    currentPlatform,
    currentPlatformItem,
    status,
    setCurrentPlatform,
    platformSwitchGuard,
  } = usePlatformContext();
  const [pendingPlatform, setPendingPlatform] = useState<typeof currentPlatform | null>(null);
  const label =
    currentPlatformItem?.display_name ??
    (status === 'loading' ? t('portal.platform.loading') : t('portal.platform.unavailable'));

  const switchLocked = platformSwitchGuard?.pending ?? false;

  function requestPlatformChange(value: string) {
    const platform = platforms.find((item) => item.code === value);
    if (!platform || platform.code === currentPlatform) return;
    if (platformSwitchGuard?.dirty) {
      setPendingPlatform(platform.code);
      return;
    }
    setCurrentPlatform(platform.code);
  }

  function confirmPlatformChange() {
    if (!pendingPlatform) return;
    platformSwitchGuard?.discard();
    setCurrentPlatform(pendingPlatform);
    setPendingPlatform(null);
  }

  return (
    <>
      <span title={switchLocked ? t('portal.platform.generationLocked') : undefined}>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="default"
                className="portal-platform-trigger"
                disabled={platforms.length === 0 || switchLocked}
                aria-label={
                  switchLocked
                    ? t('portal.platform.generationLocked')
                    : t('portal.platform.current', { platform: label })
                }
              />
            }
          >
            {switchLocked ? (
              <LoaderCircle className="animate-spin" aria-hidden="true" />
            ) : (
              <Globe2 aria-hidden="true" />
            )}
            <span className="portal-platform-trigger__label">{label}</span>
            <ChevronDown aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="portal-platform-menu" align="end">
            <DropdownMenuRadioGroup
              value={currentPlatform ?? undefined}
              onValueChange={requestPlatformChange}
            >
              <DropdownMenuLabel>
                {status === 'stale' ? t('portal.platform.cached') : t('portal.platform.label')}
              </DropdownMenuLabel>
              {platforms.map((platform) => (
                <DropdownMenuRadioItem key={platform.code} value={platform.code}>
                  {platform.display_name}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
      <Dialog
        open={pendingPlatform !== null}
        onOpenChange={(open) => {
          if (!open) setPendingPlatform(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('portal.platform.unsaved.title')}</DialogTitle>
            <DialogDescription>{t('portal.platform.unsaved.description')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={() => setPendingPlatform(null)}
            >
              {t('portal.platform.unsaved.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="default"
              onClick={confirmPlatformChange}
            >
              {t('portal.platform.unsaved.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
